'use client'

import { forwardRef } from 'react'
import { cn } from '@nwwp/ui-shadcn'

type Option = { value: string; label: string }

type Props = {
  id: string
  label: string
  name: string
  value: string | undefined
  onChange: (value: string) => void
  options: Option[]
  error?: string
  hint?: string
  onBlur?: () => void
  /** Empty-state option text. Defaults to `label`, which doubles as the placeholder. */
  placeholder?: string
  /** Replaces the visually-hidden label styling when the design shows a real label. */
  labelClassName?: string
  /** Merged into the select, for designs whose input chrome differs. */
  className?: string
}

export const RangeSelectInput = forwardRef<HTMLSelectElement, Props>(function RangeSelectInput(
  { id, label, name, value, onChange, options, error, hint, onBlur, placeholder, labelClassName, className },
  ref
) {
  return (
    <div className="flex flex-col gap-2">
      <label htmlFor={id} className={labelClassName ?? 'sr-only'}>
        {label}
      </label>
      <div className="relative">
        <select
          id={id}
          name={name}
          ref={ref}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          aria-invalid={!!error}
          className={cn(
            'w-full appearance-none rounded-sm bg-input px-4 py-3 pr-10 text-body',
            'shadow-[0_0_0_2px_var(--color-smoke)] transition-shadow',
            'hover:shadow-[0_0_0_2px_var(--color-ring)]',
            'focus:shadow-[0_0_0_2px_var(--color-ring)] focus:outline-none',
            'aria-invalid:shadow-[0_0_0_2px_var(--color-destructive)]',
            value ? 'text-black' : 'text-black/70',
            className
          )}
        >
          <option value="" disabled>
            {placeholder ?? label}
          </option>
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-neutral-darker"
          aria-hidden="true"
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </div>
      {hint && <p className="text-subtitle text-neutral-darker">{hint}</p>}
      {error && <p className="text-subtitle text-red">{error}</p>}
    </div>
  )
})
